import { type ReactNode } from 'react'

import { Outlet, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import BellLink from 'components/UI/BellLink/BellLink'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { useHeaderNav } from 'context/headerNavContext'
import { usePageTitle } from 'context/pageTitleContext'
import Footer from 'features/Footer/Footer'
import Header from 'features/Header/Header'
import SidebarNavigation from 'features/SidebarNavigation/SidebarNavigation'
import { useAccessToken, useUserProfile } from 'store'
import screens from 'styles/screens'

interface BasicLayoutProps {
    title?: string | ReactNode
    handleGoBack?: () => void
    hideBackButton?: boolean
    headerRightEl?: ReactNode
    customClass?: string
    customContentClass?: string
    children?: ReactNode
    showFooterOnThisBranch?: boolean
    isDiscoverView?: boolean
}

const DEFAULT_DESKTOP_GRID_TEMPLATE = '32rem minmax(30rem, 64rem) 32rem'
const CHAT_DESKTOP_GRID_TEMPLATE = '32rem 32rem minmax(30rem, 64rem)'

const BasicLayout = ({
    title,
    children,
    showFooterOnThisBranch,
    isDiscoverView,
}: BasicLayoutProps & { children?: React.ReactNode }) => {
    const location = useLocation()
    const path = location.pathname
    const routeKey = path.split('/')[1]

    const userData = useUserProfile()
    const { headerNav } = useHeaderNav()
    const isAuthenticated = useAccessToken() !== ''
    const { title: contextTitle } = usePageTitle()

    const {
        handleGoBack,
        hideBackButton,
        headerRightEl,
        hideFooter,
        backgroundColor,
    } = useHeaderLayout()

    const safeHeaderNav = headerNav ?? []
    const shouldShowFooter = !hideFooter && (isAuthenticated || showFooterOnThisBranch)
    const isPublicProfileView = !isAuthenticated && showFooterOnThisBranch

    const isChatListRoute = path === '/chat'
    const isSingleChatRoute = path.startsWith('/chat/') && path !== '/chat'
    const isChatAreaRoute = isChatListRoute || isSingleChatRoute

    const contentWrapperClassName = [
        'ContentWrapper',
        isChatAreaRoute ? 'layout-chat' : '',
        isSingleChatRoute ? 'layout-single-chat' : '',
    ]
        .filter(Boolean)
        .join(' ')

    const desktopGridTemplate = isChatAreaRoute
        ? CHAT_DESKTOP_GRID_TEMPLATE
        : DEFAULT_DESKTOP_GRID_TEMPLATE
const isNewPostCreateRoute = path === '/new/post/create'
    const layoutBackground =
        backgroundColor ?? (
            (
                routeKey === 'earnings' ||
                path === '/media_categories' ||
                path === '/messages/new' ||
                path === '/chat' ||
                path.startsWith('/chat/') ||
                path === '/playlists' ||
                path === '/vault' ||
                path === '/schedule' ||
                path === '/friends' ||
                path === '/transactions' ||
                path === '/creators' ||
                path === '/notifications' ||
                path.startsWith('/model/') ||
                path.startsWith('/settings') ||
                path.startsWith('/how-to/') ||
                path.startsWith('/fans') ||
                path.startsWith('/support') ||
                path.startsWith('/streams') ||
                path.startsWith('/subscriptions') ||
                path.startsWith('/analytics') ||
                path.startsWith('/user-activity/') 
            )
                ? 'linear-gradient(90deg, #f6f6f6 0%, #ffffff 51.09%, #efefef 100%)'
                : '#fff'
        )

    return (
    <PageLayout
        className="PageLayout"
        $backgroundColor={layoutBackground}
        $isAuthenticated={isAuthenticated}
    >
            <div className={isAuthenticated ? '' : 'guest-header'}>
                <Header
                    title={contextTitle && contextTitle !== '' ? contextTitle : (title ?? '')}
                    headerNav={safeHeaderNav}
                    linkBack={handleGoBack}
                    hideBackButton={hideBackButton}
                    rightElement={
                        headerRightEl ?? (userData?.role === 'fan' && <BellLink />)
                    }
                />
            </div>

            {isAuthenticated && (
                <MobileHeaderShapeMask className="mobile-header-shape-mask" />
            )}

            <MobileRoundedLayer
                className="mobile-rounded-layer"
                $isPublicProfileView={isPublicProfileView}
                $isDiscoverView={isDiscoverView}
            />

            <ContentWrapper
                className={contentWrapperClassName}
                $desktopGridTemplate={desktopGridTemplate}
                $isPublicProfileView={isPublicProfileView}
                $isNewPostCreateRoute={isNewPostCreateRoute}
            >
                <SidebarWrapper>
                    {(isAuthenticated || showFooterOnThisBranch) && <SidebarNavigation />}
                </SidebarWrapper>

                {children ?? <Outlet />}
            </ContentWrapper>

            {shouldShowFooter && <Footer />}
        </PageLayout>
    )
}

export default BasicLayout

const PageLayout = styled.div<{
    $backgroundColor?: string
    $isAuthenticated?: boolean
}>`
    @media (max-width: 949px) {
        > .guest-header {
            display: none !important;
        }
    }
    background: ${({ $backgroundColor }) => $backgroundColor ?? '#fff'};
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    height: auto;
    box-sizing: border-box;

    @media (max-width: ${screens.medium}) {
        display: flex;
        flex-direction: column;
        overflow: visible;

        padding-top: ${({ $isAuthenticated }) =>
            $isAuthenticated ? '5rem' : '0'} !important;

        &:has(.header--withnav) {
            padding-top: ${({ $isAuthenticated }) =>
                $isAuthenticated ? '12rem' : '0'} !important;
        }
    }

    @media (min-width: ${screens.medium}) {
        min-height: 100dvh;
        height: auto;
        overflow: visible;

        > .guest-header,
        > div:first-child {
            position: sticky;
            top: 0;
            z-index: 9;
            flex-shrink: 0;
            width: 100%;
            background: #fff;
        }
    }

    @media (min-width: ${screens.medium}) {
        min-height: 100dvh;
        height: auto;
        overflow: visible;
        
    }

    &.home {
        background: #fff;
    }
`

const MobileHeaderShapeMask = styled.div`
    display: none;

    @media (max-width: ${screens.medium}) {
        display: block;
        position: sticky;
        top: 4rem;
        z-index: 0;
        height: 7rem;
        margin-bottom: -7rem;
        background: #070707;
        pointer-events: none;
        flex-shrink: 0;
    }
`

const MobileRoundedLayer = styled.div<{
    $isPublicProfileView?: boolean
    $isDiscoverView?: boolean
}>`
    display: none;

    @media (max-width: ${screens.medium}) {
        display: ${({ $isPublicProfileView, $isDiscoverView }) =>
            $isPublicProfileView || $isDiscoverView ? 'none' : 'block'};

        position: relative;
        height: 7rem;
        margin-top: 0;
        margin-bottom: -7.1rem;
        background: #fff;
        border-radius: 3.3rem 3.3rem 0 0;
        pointer-events: none;
        flex-shrink: 0;

        &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: -2px;
            height: 3px;
            background: #fff;
        }
    }
`

interface ContentWrapperProps {
    $desktopGridTemplate: string
    $isPublicProfileView?: boolean
    $isNewPostCreateRoute?: boolean
}

const ContentWrapper = styled.div<ContentWrapperProps>`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    position: relative;
    background: transparent;
    border-radius: ${({ $isPublicProfileView }) =>
        $isPublicProfileView ? '0' : '2.5rem 2.5rem 0 0'};

@media (max-width: ${screens.medium}) {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    overflow: visible;

    border-radius: ${({ $isPublicProfileView }) =>
        $isPublicProfileView ? '0' : '2.8rem 2.8rem 0 0'};

    &:not(.layout-chat):not(.layout-single-chat) > :nth-child(2) {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
    }
}
    @media (min-width: ${screens.medium}) {
        display: grid;
        grid-template-columns: ${({ $desktopGridTemplate }) => $desktopGridTemplate};
        justify-content: center;
        align-items: start;
        background-color: white;
        overflow: visible;
        border-radius: 0;
        min-height: calc(100dvh - 7rem);
        
        & > div {
            min-height: 0;
            overflow: visible;
        }

        & > div:first-child,
        & > div:nth-child(3) {
            position: sticky;
            top: 7rem;
            height: calc(100dvh - 7rem);
            overflow: hidden;
        }
& > div:nth-child(2) {
    min-height: calc(100dvh - 8rem);
    overflow: visible;
}

${({ $isNewPostCreateRoute }) =>
    $isNewPostCreateRoute &&
    `
        & > :nth-child(2) {
            height: min(calc(100dvh - 8rem), 950px) !important;
            min-height: 0 !important;
            max-height: 950px !important;
            overflow-y: auto !important;
        }
    `}

        &.layout-chat > div:nth-child(2),
        &.layout-single-chat > div:nth-child(2) {
            min-height: calc(100dvh - 8rem);
            height: calc(100dvh - 8rem);
            overflow: hidden;
        }

        &.layout-chat > div:nth-child(3),
        &.layout-single-chat > div:nth-child(3) {
            position: sticky;
            top: 7rem;
            height: calc(100dvh - 8rem);
            overflow: hidden;
        }
    }
`

const SidebarWrapper = styled.div`
    overflow-y: auto;
    min-height: 0;

    @media (min-width: ${screens.medium}) {
        overflow: hidden;
        height: 100%;
        margin-bottom: 0;
    }
`